// Transit investment panel. Two views:
//   modelled  scenario 5: the lines scenario 3's tolls pay for, as the model picked them
//   explore   mode switch, same-budget presets, two sliders, headline numbers, ranked list (illustrative)
// Self-contained: all data and callbacks come in as props, so it can be mounted on the map
// or inside another screen (for example a scenario view) without changes.
import React, { useId } from 'react';
import { PICKED_BY } from '../utils/transitScenarioLayers';

const VIEWS = [
  { key: 'modelled', label: 'Scenario 5 (modelled)' },
  { key: 'explore', label: 'Explore (illustrative)' },
];

const MODES = [
  { key: 'lines', label: 'Selected lines', hint: 'Hexagons are shaded by low-income share. Yellow lines are the ones chosen for more service.' },
  { key: 'access', label: 'Before / after', hint: 'Teal hexagons gain weekday trips within a walk. Orange outlines mark mostly low-income areas that gain.' },
  { key: 'gaps', label: 'Service gaps', hint: 'Mostly low-income hexagons with under 100 weekday trips within a walk: red stays underserved, teal is closed by the investment.' },
];

const fmt = (n) => Math.round(n).toLocaleString();
const USD0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const USDK = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 });
const MIN = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const rgb = (c) => `rgb(${c.join(',')})`;
const pct = (boost) => Math.round(boost * 100);

function PickBadge({ by }) {
  const p = PICKED_BY[by];
  if (!p) return null;
  return <span className="inv-pick" style={{ color: rgb(p.color), borderColor: rgb(p.color) }} title={`Picked by ${p.what}`}>{p.label}</span>;
}

function LineRow({ l }) {
  return (
    <tr>
      <td className="inv-rank">{l.order}</td>
      <th scope="row" className="inv-s5-name">
        <span className="inv-s5-line">{l.line}</span>
        <span className="inv-s5-sub">{l.agency} <PickBadge by={l.picked_by} /></span>
      </th>
      <td>{MIN.format(l.peak_headway_min)}→{MIN.format(l.new_headway_min)}</td>
      <td>{USDK.format(l.extra_cost_per_weekday_usd)}</td>
      <td>{fmt(l.marginal_shifts_per_weekday)}</td>
    </tr>
  );
}

function agencyText(byAgency) {
  const rows = Object.entries(byAgency).sort((a, b) => b[1] - a[1]);
  const many = rows.filter(([, n]) => n > 1).map(([a, n]) => `${a.replace(/^SFMTA \((.+)\)$/, '$1')} ${n}`);
  const ones = rows.filter(([, n]) => n === 1).length;
  return [...many, ones ? `${ones} other ${ones === 1 ? 'agency' : 'agencies'} 1 each` : null].filter(Boolean).join(', ');
}

/** Scenario 5's transit half: the 36 lines, what they cost against the tolls, and how the
 *  budget chose them. Figures are per weekday, over the six charged hours. */
function ModelledView({ upgrades, rank }) {
  const t = upgrades.totals;
  const m = upgrades.method;
  const lines = [...upgrades.lines].sort((a, b) => a.order - b.order);
  const unfunded = rank ? rank.lines.filter((l) => !l.picked_by) : [];
  const candidates = m.lines_ranked + Object.keys(m.lines_below_headway_floor || {}).length;
  return (
    <>
      <div className="inv-model-head">
        <span>Scenario 3&apos;s tolls buy {Math.round(m.boost * 100)}% more departures on {t.lines_picked} lines into the zone.</span>
        <span className="inv-model-tag" title="From the traffic model's trips and the mode-shift model">Model</span>
      </div>
      <div className="inv-stats">
        <div><b>{t.lines_picked}</b><span>lines: {agencyText(t.by_agency)}</span></div>
        <div><b>{USD0.format(t.extra_cost_per_weekday_usd)}</b><span>a weekday, of {USD0.format(m.budget_usd_per_weekday)} in tolls</span></div>
        <div><b>+{fmt(t.expected_extra_shifts_per_weekday)}</b><span>more car trips switch to transit (expected), beyond scenario 3&apos;s {fmt(t.removed_by_toll_per_weekday)}</span></div>
        <div><b>{fmt(t.extra_vehicle_hours_per_weekday)}</b><span>extra vehicle hours a weekday</span></div>
        <div className="inv-stats-wide"><b>{fmt(t.served_trips_per_weekday)}</b><span>car trips into the zone start and end within {Math.round(m.walk_m)} m of an upgraded line</span></div>
      </div>
      <div className="inv-method">
        Candidates: the {candidates} lines that stop within {Math.round(m.walk_m)} m of the zone. The budget goes down
        the list by expected switches per dollar, with {Math.round(m.equity_share_of_budget * 100)}% kept for the lines
        reaching the most low-income households per dollar. Waiting counts as half the headway, at most{' '}
        {MIN.format(m.max_wait_min)} min, so {Object.keys(m.lines_below_headway_floor || {}).length} infrequent lines
        save no waiting. A trip served at both ends weighs the shorter wait at {MIN.format(m.wait_weight)}× in-vehicle time in
        scenario 3&apos;s mode choice; drivers who stay keep scenario 3&apos;s routes.
      </div>
      <div className="inv-legend">
        {Object.entries(PICKED_BY).map(([key, p]) => (
          <span key={key}><span className="inv-swatch" style={{ background: rgb(p.color) }} />{p.label === 'equity' ? 'Equity share' : 'Switches per dollar'}</span>
        ))}
        <span className="inv-legend-note">Hexagons: low-income share.</span>
      </div>
      <div className="inv-s5-list">
        <table>
          <thead>
            <tr>
              <th scope="col">#</th><th scope="col">Line</th><th scope="col" title="Peak headway, minutes">Headway</th>
              <th scope="col" title="Extra cost a weekday">Cost</th><th scope="col" title="Expected extra switches to transit a weekday">Switch</th>
            </tr>
          </thead>
          <tbody>{lines.map((l) => <LineRow key={l.id} l={l} />)}</tbody>
        </table>
      </div>
      {unfunded.length > 0 && (
        <details className="inv-details">
          <summary className="inv-list-title">{unfunded.length} more candidates the budget did not reach</summary>
          <div className="inv-s5-list">
            <table>
              <thead>
                <tr>
                  <th scope="col">Line</th><th scope="col">Headway</th><th scope="col">Cost</th>
                  <th scope="col" title="Expected switches to transit a weekday, had it been funded">Switch</th>
                </tr>
              </thead>
              <tbody>
                {unfunded.map((l) => (
                  <tr key={l.id}>
                    <th scope="row" className="inv-s5-name">
                      <span className="inv-s5-line">{l.line}</span><span className="inv-s5-sub">{l.agency}</span>
                    </th>
                    <td>{MIN.format(l.peak_headway_min)}→{MIN.format(l.new_headway_min)}</td>
                    <td>{USDK.format(l.extra_cost_per_weekday_usd)}</td>
                    <td>{fmt(l.standalone_shifts_per_weekday)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
      <div className="inv-foot">
        Switches are expected values over the six charged hours.
        {upgrades.uncapped && (
          <>
            {' '}Without the {MIN.format(m.max_wait_min)}-minute cap on waiting, the same budget would pick{' '}
            {upgrades.uncapped.lines_picked} lines and expect {fmt(upgrades.uncapped.expected_extra_shifts_per_weekday)} switches,
            most on infrequent lines whose riders time their arrival.
          </>
        )}
        {' '}Costs: NTD 2024 operating cost per vehicle hour. Schedules: 511.org.
      </div>
    </>
  );
}

const SAME_BUDGET_HEAD = (
  <thead>
    <tr>
      <th scope="col">Extra service</th>
      <th scope="col">Lines</th>
      <th scope="col">Extra weekday trips</th>
      <th scope="col">Low-income households better off</th>
      <th scope="col">Underserved hexagons</th>
    </tr>
  </thead>
);

/** One extra weekday cost spent three ways: a button applies one, the table puts their figures side by side. */
function SameBudget({ eq, error, onRetry, active, onPick }) {
  const titleId = useId();
  if (!eq) {
    return (
      <div className="inv-eq inv-eq-status" aria-live="polite">
        {error ? (
          <>
            <div className="overlay-legend-error">Same-budget scenarios could not be loaded ({error}).</div>
            <button type="button" className="inv-retry" onClick={onRetry}>Try again</button>
          </>
        ) : 'Loading same-budget scenarios…'}
      </div>
    );
  }
  return (
    <div className="inv-eq">
      <div className="inv-eq-title" id={titleId}>Same budget</div>
      <div className="inv-eq-note">
        Same extra cost, about {USDK.format(eq.budget)} a weekday{eq.ntdYear ? ` (NTD\u00a0${eq.ntdYear} unit costs)` : ''}: lines
        are taken in reach order, skipping any the remaining budget cannot cover.
      </div>
      <div className="inv-modes inv-eq-presets" role="group" aria-labelledby={titleId}>
        {eq.presets.map((p) => (
          <button key={p.key} type="button" aria-pressed={active === p.key} className={active === p.key ? 'active' : ''}
            onClick={() => onPick(p)}>
            +{pct(p.boost)}% on{'\u00a0'}{p.funded}{'\u00a0'}lines
          </button>
        ))}
      </div>
      <table className="inv-eq-table" aria-labelledby={titleId}>
        {SAME_BUDGET_HEAD}
        <tbody>
          {eq.presets.map((p) => (
            <tr key={p.key} className={active === p.key ? 'active' : undefined}>
              <th scope="row">+{pct(p.boost)}%</th>
              <td>{p.stats.linesFunded}</td>
              <td>+{fmt(p.stats.extraTrips)}</td>
              <td>{fmt(p.stats.lowBetter)}</td>
              <td>{fmt(p.stats.gapBefore)} → {fmt(p.stats.gapAfter)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExploreView({
  lines, order, metric, onMetricChange, meta, sel, cum, stats, mode, onModeChange, target, onTargetChange, boost, onBoostChange,
  sameBudget, sameBudgetError, onSameBudgetRetry, preset, onPresetChange,
}) {
  const field = metric === 'reach' ? 'low_income_underserved_reach' : 'low_income_households_served';
  const shown = order.slice(0, 14);
  const maxLow = lines.length ? Math.max(1, lines[order[0]][field]) : 1;
  const hint = (MODES.find((m) => m.key === mode) || MODES[0]).hint;
  const applied = sameBudget && preset ? sameBudget.presets.find((p) => p.key === preset) : null;
  return (
    <>
      <div className="inv-modes">
        {MODES.map((m) => (
          <button key={m.key} type="button" className={mode === m.key ? 'active' : ''} onClick={() => onModeChange(m.key)}>
            {m.label}
          </button>
        ))}
      </div>
      <div className="inv-hint">{hint}</div>

      {onPresetChange ? (
        <SameBudget eq={sameBudget} error={sameBudgetError} onRetry={onSameBudgetRetry} active={preset} onPick={onPresetChange} />
      ) : null}

      <label className="inv-slider">
        <span>Share of service chosen for investment <b>{target}%</b></span>
        <input type="range" min="2" max="45" step="1" value={target} onChange={(e) => onTargetChange(+e.target.value)} />
      </label>
      <label className="inv-slider">
        <span>Extra service on those lines <b>+{Math.round(boost * 100)}%</b></span>
        <input type="range" min="0.1" max="1" step="0.05" value={boost} onChange={(e) => onBoostChange(+e.target.value)} />
      </label>

      <div className="inv-illustrative" role="note">
        <strong>Illustrative scenario, not modelled</strong>
        <span>
          {applied ? (
            <>
              Same-budget scenario: about {USDK.format(sameBudget.budget)} a weekday buys {pct(applied.boost)}% more trips
              on each of {applied.funded} lines, taken in reach order.
            </>
          ) : (
            <>Assumption: the lines carrying {target}% of weekday trips each get {Math.round(boost * 100)}% more trips.</>
          )}
        </span>
      </div>
      <div className="inv-stats">
        <div><b>{stats.linesFunded}</b><span>lines funded ({cum.toFixed(1)}% of service)</span></div>
        <div><b>+{fmt(stats.extraTrips)}</b><span>weekday trips ({stats.extraPct.toFixed(1)}% more service)</span></div>
        <div><b>{fmt(stats.lowBetter)}</b><span>low-income households with better access</span></div>
        {mode === 'gaps'
          ? <div><b>{stats.gapBefore} → {stats.gapAfter}</b><span>underserved low-income hexagons</span></div>
          : <div><b>{fmt(stats.lowAvgBefore)} → {fmt(stats.lowAvgAfter)}</b><span>weekday trips within a walk, average low-income household</span></div>}
      </div>

      <div className="inv-modes">
        <button type="button" className={metric === 'reach' ? 'active' : ''} onClick={() => onMetricChange('reach')}>Rank by gap reach</button>
        <button type="button" className={metric === 'count' ? 'active' : ''} onClick={() => onMetricChange('count')}>Rank by households</button>
      </div>
      <details className="inv-details">
      <summary className="inv-list-title">
        {metric === 'reach'
          ? 'Show lines ranked by low-income households in thinly served areas they reach'
          : `Show lines ranked by low-income households within a ${meta.walk_metres} m walk`}
      </summary>
      <div className="inv-list">
        {shown.map((li, pos) => {
          const l = lines[li];
          return (
            <div key={`${l.agency}-${l.line}`} className={`inv-row${sel.has(li) ? ' sel' : ''}`}>
              <span className="inv-rank">{pos + 1}</span>
              <span className="inv-name" title={`${l.line} · ${l.agency}`}>{l.line} <i>{l.agency}</i></span>
              <span className="inv-bar"><span style={{ width: `${(100 * l[field]) / maxLow}%` }} /></span>
              <span className="inv-num">{(l[field] / 1000).toFixed(0)}k</span>
            </div>
          );
        })}
      </div>
      </details>
    </>
  );
}

export default function TransitInvestmentPanel({
  view = 'modelled', onViewChange, upgrades = null, rank = null, meta, onClose,
  className = '', style, onMouseDown, collapseToggle = null, panelRef = null, ...explore
}) {
  const modelled = view === 'modelled' && upgrades;
  return (
    <div ref={panelRef} className={`inv-panel ${modelled ? 'inv-panel-wide' : ''} ${className}`} style={style} onMouseDown={onMouseDown}>
      <div className="inv-head">
        <span>Transit investment</span>
        <span className="panel-head-actions">
          {collapseToggle}
          <button type="button" className="inv-close" aria-label="Close" onClick={onClose}>×</button>
        </span>
      </div>

      {upgrades && (
        <div className="inv-modes inv-views" role="group" aria-label="Transit investment view">
          {VIEWS.map((v) => (
            <button key={v.key} type="button" aria-pressed={view === v.key} className={view === v.key ? 'active' : ''}
              onClick={() => onViewChange(v.key)}>
              {v.label}
            </button>
          ))}
        </div>
      )}

      {modelled
        ? <ModelledView upgrades={upgrades} rank={rank} />
        : <ExploreView meta={meta} {...explore} />}

      <div className="inv-foot">
        Low income = below 80% of the Bay Area median household income
        {meta.low_income_below ? ` (under $${meta.low_income_below.toLocaleString()})` : ''},
        from ACS 2019–23 block groups split to hexes by area. Trips are weekday departures.
      </div>
    </div>
  );
}
